import { keycloakConfig, keycloakInitOptions } from 'configs/keycloak-config';
import Keycloak from 'keycloak-js';
import { KeycloakProps, authenticationFailed, logIn } from 'slices/navigation/navigationSlice';
import { StoreDispatch, initReduxState } from 'store/store';

export type Tokens = Pick<KeycloakProps, 'token' | 'refreshToken' | 'idToken'>;

export default class KC {
  private kc: Keycloak;

  constructor(
    private readonly setKeycloakProps: (keycloakProps: KeycloakProps) => void,
    private readonly dispatch: StoreDispatch,
    tokens?: Tokens
  ) {
    this.kc = new Keycloak(keycloakConfig);

    if (tokens) this.init(tokens);

    this.setEvents();
  }

  setEvents() {
    const { kc, dispatch, onAuthSuccess } = this;
    // On first successful login
    kc.onAuthSuccess = () => onAuthSuccess(true);

    kc.onAuthError = (err) => {
      dispatch(authenticationFailed());
      // eslint-disable-next-line
      console.error('Authentication Failed', err);
    };

    // When token has been successfully refreshed
    kc.onAuthRefreshSuccess = () => onAuthSuccess(false);

    kc.onAuthRefreshError = () => {
      const isOffline = sessionStorage.getItem('offline') === 'true';
      if (!isOffline) {
        // eslint-disable-next-line
        dispatch(authenticationFailed());
        console.error('Token Refresh Failed');
      }
    };

    kc.onTokenExpired = () => {
      kc.updateToken();
    };
  }

  async init(tokens?: Tokens) {
    try {
      const authenticated = await this.kc.init({ ...keycloakInitOptions, ...tokens });

      // If already authenticated after init (e.g., after redirect from Keycloak),
      // manually trigger authentication state update since events might not fire reliably
      if (authenticated && this.kc.token) {
        await this.kc.loadUserProfile();
        const keycloakProps = this.keycloakProps;
        this.setKeycloakProps(keycloakProps);
        this.dispatch(logIn(keycloakProps));

        // Initialize Redux state after authentication is complete
        setTimeout(() => initReduxState(), 0);
      }
    } catch (error) {
      console.error('Keycloak initialization failed:', error);
      this.dispatch(authenticationFailed());
    }
  }

  private onAuthSuccess = (initRedux: boolean) => {
    this.kc.loadUserProfile().then(() => {
      const keycloakProps = this.keycloakProps;

      this.setKeycloakProps(keycloakProps);
      this.dispatch(logIn(keycloakProps));

      // Initialize Redux state asynchronously without blocking authentication
      if (initRedux) {
        setTimeout(() => initReduxState(), 0);
      }
    });
  };

  get token() {
    return this.kc.token ?? '';
  }

  get bearerToken() {
    return this.kc.token ? `Bearer ${this.kc.token}` : '';
  }

  get keycloakProps(): KeycloakProps {
    return {
      isAuthenticated: this.kc.authenticated ?? false,
      profile: this.kc.profile,
      token: this.kc.token,
      tokenParsed: this.kc.tokenParsed,
      refreshToken: this.kc.refreshToken,
      idToken: this.kc.idToken,
    };
  }

  get profile() {
    return this.kc.profile;
  }

  get username() {
    return this.kc.profile?.username ?? '';
  }

  logout() {
    this.kc.logout();
  }
}
